/**
 * Monitoring Page
 * Service health monitoring dashboard with tabs for Services, Cache, and Storage
 */

import { useState } from "react";
import { Loading } from "@/components/common";
import { Card } from "@/components/ui/Card";
import {
  ServiceHealthCard,
  CacheMetricsPanel,
  CacheDetailsPanel,
  RefreshControl,
  HitRateGauge,
  LatencyChart,
  RequestsChart,
  CanaryStatusPanel,
  QueueTab,
} from "@/features/monitoring";
import { StorageTab } from "@/features/resources";
import { useMonitoringDashboard } from "@/features/monitoring/hooks/useMonitoring";
import { useI18n } from "@/i18n";
import {
  AlertCircle,
  Activity,
  Server,
  HardDrive,
  Database,
  Inbox,
} from "lucide-react";

type TabType = "services" | "cache" | "storage" | "queue";

export function MonitoringPage() {
  const { messages } = useI18n();
  const t = messages.monitoring;

  const [activeTab, setActiveTab] = useState<TabType>("services");
  const [refreshInterval, setRefreshInterval] = useState(10000); // Default: 10s
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  const {
    data: dashboard,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useMonitoringDashboard({
    refetchInterval: autoRefreshEnabled ? refreshInterval : 0,
    enabled: true,
  });

  const handleManualRefresh = () => {
    refetch();
  };

  const handleCacheCleared = () => {
    refetch();
  };

  const tabs: { key: TabType; label: string; icon: React.ReactNode }[] = [
    {
      key: "services",
      label: t.tabs?.services || "服务健康",
      icon: <Server className="w-4 h-4" />,
    },
    {
      key: "cache",
      label: t.tabs?.cache || "缓存",
      icon: <Database className="w-4 h-4" />,
    },
    {
      key: "storage",
      label: t.tabs?.storage || "存储",
      icon: <HardDrive className="w-4 h-4" />,
    },
    {
      key: "queue",
      label: t.tabs?.queue || "提取队列",
      icon: <Inbox className="w-4 h-4" />,
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Activity className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t.pageTitle}</h1>
            <p className="text-sm text-gray-500 mt-1">{t.pageDescription}</p>
          </div>
        </div>

        {/* Refresh Controls - only show for services/cache tabs */}
        {(activeTab === "services" || activeTab === "cache") && (
          <RefreshControl
            interval={refreshInterval}
            enabled={autoRefreshEnabled}
            isRefreshing={isFetching}
            onIntervalChange={setRefreshInterval}
            onEnabledChange={setAutoRefreshEnabled}
            onRefresh={handleManualRefresh}
          />
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Services & Cache Tab Content */}
      {(activeTab === "services" || activeTab === "cache") && (
        <>
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loading message={messages.common.loading} />
            </div>
          )}

          {/* Error State */}
          {isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-1">
                  {t.errors.loadFailed}
                </h3>
                <p className="text-sm text-red-700">
                  {error instanceof Error
                    ? error.message
                    : messages.common.error}
                </p>
                <button
                  onClick={handleManualRefresh}
                  className="mt-3 text-sm font-medium text-red-600 hover:text-red-700 underline"
                >
                  {t.errors.tryAgain}
                </button>
              </div>
            </div>
          )}

          {/* Dashboard Content */}
          {dashboard && (
            <div className="space-y-6">
              {/* Last Update Timestamp */}
              <div className="text-sm text-gray-500">
                {t.lastUpdated}:{" "}
                {new Date(dashboard.timestamp).toLocaleString()}
              </div>

              {/* Services Tab Content */}
              {activeTab === "services" && (
                <>
                  {/* Service Status Cards - Grid with Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {dashboard.services.map((service) => (
                      <ServiceHealthCard key={service.id} service={service} />
                    ))}
                    {/* Summary Card */}
                    <Card className="bg-white">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Server className="w-5 h-5 text-gray-400" />
                          <span className="text-sm font-medium text-gray-600">
                            {t.serviceHealth.services}
                          </span>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {dashboard.summary.healthy_services}/
                          {dashboard.summary.total_services}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            dashboard.summary.healthy_services ===
                            dashboard.summary.total_services
                              ? "bg-green-500"
                              : "bg-yellow-500"
                          }`}
                        />
                        <span className="text-2xl font-bold text-gray-900">
                          {dashboard.summary.healthy_services ===
                          dashboard.summary.total_services
                            ? t.serviceHealth.allOk
                            : t.serviceHealth.issues}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        {t.serviceHealth.overallStatus}
                      </p>
                    </Card>
                  </div>

                  {/* Canary Deployment Status */}
                  <CanaryStatusPanel />
                </>
              )}

              {/* Cache Tab Content */}
              {activeTab === "cache" && (
                <>
                  {/* Charts Row */}
                  {dashboard.cache && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <HitRateGauge hitRate={dashboard.cache.hit_rate} />
                      <LatencyChart
                        avgLatency={dashboard.cache.avg_latency_ms}
                        cacheLatency={dashboard.cache.cache_latency_ms}
                        backendLatency={dashboard.cache.backend_latency_ms}
                      />
                      <RequestsChart
                        hits={dashboard.cache.hits}
                        misses={dashboard.cache.misses}
                        errors={dashboard.cache.errors}
                      />
                    </div>
                  )}

                  {/* Cache Metrics Overview */}
                  <CacheMetricsPanel metrics={dashboard.cache} />

                  {/* Cache Details with Extended Metrics */}
                  <CacheDetailsPanel
                    metrics={dashboard.cache}
                    onCacheCleared={handleCacheCleared}
                  />
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Storage Tab Content */}
      {activeTab === "storage" && <StorageTab />}

      {/* Queue Tab Content */}
      {activeTab === "queue" && (
        <QueueTab
          autoRefreshEnabled={autoRefreshEnabled}
          refreshInterval={refreshInterval}
        />
      )}
    </div>
  );
}
